import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { useError } from '../Contexts/ErrorContext';
import ExcelWrapper from '../../../utils/excelWrapper.js';
import QueryStringService from '../../../services/queryStringService.js';
import dateFunctions from '../../../utils/dateFunctions.js';
import { saveAs } from 'file-saver';

/**
 * Core utility functions to be used by both the hook and directly by ExportManager
 */
export const ExportCoreUtils = {

    getFileExtension: (exportType, customExtension = null) => {
        if (customExtension) return customExtension;
        return exportType === 'excel' ? 'xlsx' : 'zip';
    },

    getMimeType: (exportType, customMimeType = null) => {
        if (customMimeType) return customMimeType;
        return exportType === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/zip';
    },

    handleDirectFileDownload: async (data, fileName, mimeType) => {
        const blob = data instanceof Blob ? data : new Blob([data], {
            type: mimeType
        });

        saveAs(blob, fileName);
        return data;
    },

    generateFilename: (prefix, extension, countryCode) => {
        const timestamp = dateFunctions.getCountrySpecificDateTimeFormatFromDate(
            countryCode,
            moment()
        ).replace(/[:/\\?*"<>|]/g, '-');
        return `${prefix}-${timestamp}.${extension}`;
    },

    handleExcelExport: async (data, fileName, sheetName = 'Sheet1') => {
        if (!data) {
            throw new Error('No data received for Excel export');
        }

        const isMultipleSheets = Array.isArray(data) &&
            data.length > 0 &&
            data[0] &&
            typeof data[0] === 'object' &&
            'name' in data[0] &&
            'data' in data[0] &&
            typeof data[0].name === 'string' &&
            Array.isArray(data[0].data);

        if (isMultipleSheets) {
            // Handle multiple sheets export
            await ExcelWrapper.writeMultipleSheets(fileName, data, {
                autoWidth: true
            });
        } else {
            // Handle single sheet export
            if (!Array.isArray(data)) {
                throw new Error('Invalid data format received for Excel export');
            }
            await ExcelWrapper.write(fileName, sheetName, data, {
                autoWidth: true
            });
        }

        return data;
    }
};

/**
 * React hook for handling exports
 */
export function useExport({
    fetchData,
    fileNamePrefix = 'Export',
    sheetName = 'Sheet1',
    exportType = 'excel',
    isFileDownload = false,
    mimeType = null,
    fileExtension = null,
    onExportStart = () => { },
    onExportComplete = () => { },
    onExportError = () => { }
}) {
    const [isExporting, setIsExporting] = useState(false);
    const { showError } = useError();

    const queryStringService = useMemo(() => new QueryStringService(), []);
    const countryCode = useMemo(() => queryStringService.getCurrentCountry(), [queryStringService]);

    const handleExport = useCallback(async () => {
        // If already exporting, prevent duplicate operations
        if (isExporting) return;

        try {
            setIsExporting(true);
            onExportStart();

            // Fetch the data
            const data = await fetchData();

            // Generate filename with unique timestamp
            const extension = ExportCoreUtils.getFileExtension(exportType, fileExtension);
            const fullFileName = ExportCoreUtils.generateFilename(fileNamePrefix, extension, countryCode);

            if (isFileDownload || exportType === 'zip') {
                await ExportCoreUtils.handleDirectFileDownload(
                    data,
                    fullFileName,
                    ExportCoreUtils.getMimeType(exportType, mimeType)
                );
            } else if (exportType === 'excel') {
                await ExportCoreUtils.handleExcelExport(data, fullFileName, sheetName);
            }

            onExportComplete({
                fileName: fullFileName,
                rowCount: exportType === 'excel' && !isFileDownload ? data.length : null,
                exportType: exportType
            });

            return {
                success: true,
                fileName: fullFileName
            };
        } catch (err) {
            console.error('Export failed:', err);
            showError(err.message || `Export failed. Please try again.`);
            onExportError(err);
            throw err;
        } finally {
            // Always reset the exporting state when done
            setIsExporting(false);
        }
    }, [
        isExporting,
        fetchData,
        fileNamePrefix,
        onExportStart,
        onExportComplete,
        onExportError,
        isFileDownload,
        exportType,
        showError,
        fileExtension,
        mimeType,
        sheetName,
        countryCode
    ]);

    return {
        isExporting,
        handleExport
    };
}